<script>
  import { onDestroy, onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import {
    Button,
    ButtonGroup,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Input,
    Tooltip
  } from '@sveltestrap/sveltestrap';
  import { endOfDay, subDays } from 'date-fns';

  import { SparkChart } from '@client/components/charts';
  import { DatePicker } from '@client/components/datepicker';
  import { Icon } from '@client/components/icon';
  import { Select } from '@client/components/select';

  import { AssetsService } from '@client/services/assets';
  import { CampaignsService } from '@client/services/campaigns';
  import { PluginsService } from '@client/services/plugins';
  import { TargetingService } from '@client/services/targeting';

  import { consolidateMetrics, formatRate, summarizeMetrics, sumMetricSeries } from '@client/utils/banners';
  import { determineCampaignStatus } from '@client/utils/campaigns';
  import { METRICS_REQUEST_CONCURRENCY, resolveMetricsWindow } from '@client/utils/campaigns';
  import { QueryParams } from '@client/utils/query-params';

  import { CAMPAIGN_FILTERS } from '@client/enums';
  import { COLORS, PERFORMANCE_COLORS } from '@client/enums';

  import { generatePastelColors } from '@common/utils/colors';
  import { formatDate, getDateFromToday } from '@common/utils/dates';
  import { shortenNumber } from '@common/utils/numbers';
  import { deepCopy, merge } from '@common/utils/objects';
  import { mapWithConcurrency } from '@common/utils/promise';

  import Chart from './charts/metrics';

  export let cache = {};
  export let filtered = [];
  export let selectedData = [];
  export let selectedItems = [];
  export let selectedItemField = '';
  export let startDate = '';
  export let endDate = '';
  export let updating = false;
  export let isEditView = false;
  export let daysOverride = null;
  export let statusFilters = {};
  export let update = async () => {};
  export let targeting = {};
  export let integrations = [];
  export let includeEvergreen = false;

  // Cache management constants
  const MAX_CACHE_SIZE = 100;
  const MAX_CACHE_AGE = 30 * 60 * 1000; // 30 minutes in milliseconds

  // Track CSS custom properties for cleanup
  let appliedCssProperties = new Set();

  let compareChartMetrics = {
    deliveries: [],
    impressions: [],
    clicks: [],
    basicClicks: [],
    basicImpressions: []
  };

  let campaignMetrics = {
    totalDeliveries: 0,
    totalClicks: 0,
    totalViews: 0,
    totalReach: 0,
    totalBasicViews: 0,
    totalBasicClicks: 0
  };

  let expandedChartMetrics = {
    deliveries: [],
    impressions: [],
    clicks: [],
    basicClicks: [],
    basicImpressions: []
  };

  let expandedChartData = {};

  const sparkChartData = {
    delivered: {
      color: '#43A0FC',
      label: $format('label.DELIVERED'),
      data: []
    },
    views: {
      color: '#D9BC61',
      label: $format('label.IMPRESSIONS'),
      data: []
    },
    clicks: {
      color: '#92E2C0',
      label: $format('label.CLICKS'),
      data: []
    },
    basicClicks: {
      color: '#92E2C0',
      label: $format('label.CLICKS'),
      data: []
    },
    basicViews: {
      color: '#D9BC61',
      label: $format('label.IMPRESSIONS'),
      data: []
    }
  };

  let campaignFields = [
    {
      label: $format('label.CAMPAIGNS'),
      value: 'campaigns'
    },
    {
      label: $format('label.FOLDERS'),
      value: 'folders'
    },
    {
      label: $format('label.TAGS'),
      value: 'tags'
    },
    {
      label: $format('label.CATEGORIES'),
      value: 'categories'
    }
  ];

  let sortDirection = -1;
  let sortKey = 'reach';

  let filters = merge({}, CAMPAIGN_FILTERS);

  filters.dateRange.startDate = QueryParams.get('startDate') || formatDate(getDateFromToday(59), 'MM/dd/yyyy');
  filters.dateRange.endDate = QueryParams.get('endDate') || formatDate(getDateFromToday(0), 'MM/dd/yyyy');
  filters.sorting.key = QueryParams.get('sortKey') || sortKey;
  filters.sorting.dir = QueryParams.get('sortDir') || sortDirection === -1 ? 'asc' : 'desc';

  let sparkData = deepCopy(sparkChartData);

  let queryResult = [];
  let campaigns = [];
  let otherCampaigns = [];
  let records = [];

  let folderNameMap = {};
  let folderParentMap = {};

  let selectedField = campaignFields.find((field) => field.value === selectedItemField);

  let isOpen = false;
  let isExpanded = false;

  let assignedTagColors = {};
  let tagColors = generatePastelColors(400);
  let assignedCategoryColors = {};
  let categoryColors = generatePastelColors(400);
  let dateFormat = 'MMM d, yyyy';
  let formattedStartDate = '';
  let formattedEndDate = '';

  let chartUnit = 'day';
  let chartType = 'line';

  const rangeOptions = [
    { label: $format('label.TIME_PERIOD', { values: { amount: 30, time: $format('label.DAYS') } }), value: 30 },
    { label: $format('label.TIME_PERIOD', { values: { amount: 60, time: $format('label.DAYS') } }), value: 60 },
    { label: $format('label.TIME_PERIOD', { values: { amount: 90, time: $format('label.DAYS') } }), value: 90 },
    { label: $format('label.TIME_PERIOD', { values: { amount: 6, time: $format('label.MONTHS') } }), value: 180 },
    { label: $format('label.TIME_PERIOD', { values: { amount: 1, time: $format('label.YEAR') } }), value: 365 },
    { label: $format('label.DATES_ALL_TIME'), value: 365 * 10 }
  ];

  // Custom date range (edit view only) — mutually exclusive with the preset daysOverride buttons
  let customStart = null;
  let customEnd = null;
  let customPickerOpen = false;
  $: isCustomRange = !!(customStart && customEnd);

  const selectPreset = (value) => {
    customStart = null;
    customEnd = null;
    customPickerOpen = false;
    daysOverride = value;
  };

  const onCustomDateChange = ({ startDate: start, endDate: end }) => {
    if (!start || !end) return;
    customStart = start;
    customEnd = end;
    daysOverride = null;
    customPickerOpen = false;
  };

  const determineDefaultRange = (campaign) => {
    if (!campaign?.enableschedule || !campaign?.schedule?.startDate) {
      return 365;
    }
    const daysSinceStart = Math.ceil((Date.now() - new Date(campaign.schedule.startDate).getTime()) / 86400000);
    return rangeOptions.find((option) => option.value >= daysSinceStart)?.value || 365 * 10;
  };

  const sparkFormatHeader = (value) => formatDate(new Date(value), 'EEE, MMM d, yyyy');

  // Cache management functions
  const cleanupOldCacheEntries = () => {
    const now = Date.now();
    const cacheKeys = Object.keys(cache);

    if (cacheKeys.length <= MAX_CACHE_SIZE) return;

    // Sort by timestamp (if available) or remove oldest entries
    const entriesToRemove = cacheKeys
      .map((key) => ({ key, timestamp: cache[key]?.timestamp || 0 }))
      .sort((a, b) => a.timestamp - b.timestamp)
      .slice(0, cacheKeys.length - MAX_CACHE_SIZE)
      .map((entry) => entry.key);

    entriesToRemove.forEach((key) => delete cache[key]);
  };

  const isValidCacheEntry = (cacheKey) => {
    const entry = cache[cacheKey];
    if (!entry || !entry.timestamp) return true; // Keep entries without timestamp for backward compatibility

    return Date.now() - entry.timestamp < MAX_CACHE_AGE;
  };

  const cleanupExpiredCacheEntries = () => {
    Object.keys(cache).forEach((key) => {
      if (!isValidCacheEntry(key)) {
        delete cache[key];
      }
    });
  };

  const filterDataByRange = (data) => {
    return data.filter(([dateString]) => {
      const date = new Date(dateString);

      return date >= new Date(startDate) && date <= new Date(endDate);
    });
  };

  const generateMetricDataset = (series, label, color) => {
    const labels = [];
    const points = [];

    const data = series.reduce((acc, curr) => {
      const formattedLabel = formatDate(curr[0], 'MM/dd/yyyy');

      if (!acc[formattedLabel]) {
        acc[formattedLabel] = 0;
      }

      acc[formattedLabel] += curr[1];

      return acc;
    }, {});

    const sortedSeries = Object.keys(data)
      .map((dateString) => new Date(dateString))
      .sort((a, b) => a - b);

    const sortedSeriesObject = sortedSeries.reduce((acc, date) => {
      const dateString = formatDate(date, 'MM/dd/yyyy');

      acc[dateString] = data[dateString];

      return acc;
    }, {});

    Object.keys(sortedSeriesObject).forEach((date) => {
      labels.push(date);
      points.push(sortedSeriesObject[date]);
    });

    if (series?.length) {
      return {
        labels,
        datasets: [
          {
            label,
            backgroundColor: chartType === 'bar' ? color : `${color}10`,
            borderColor: color,
            pointColor: color,
            fill: true,
            tension: 0.3,
            data: points
          }
        ]
      };
    }

    return null;
  };

  const onClearChartMetrics = () => {
    compareChartMetrics = {
      deliveries: [],
      impressions: [],
      clicks: []
    };

    expandedChartMetrics = {
      deliveries: [],
      impressions: [],
      clicks: []
    };
  };

  const assignActiveStatus = (record) => {
    const { active, completed, notscheduled, paused, schedule, scheduled, rulename, targetingSources } = record;
    const upcoming = new Date(schedule?.startDate).getTime() >= new Date().getTime();

    // Order mirrors the Campaigns manage page: paused is checked first so a recently-ended paused
    // campaign is labeled `paused`, not `expired`.
    if (paused) {
      return `paused-${rulename}`;
    }

    if (active && !upcoming) {
      return `active-scheduled-${rulename}`;
    }

    if (!!notscheduled && !!targetingSources) {
      return `active-targeting-${rulename}`;
    }

    if (active && upcoming) {
      return `upcoming-${rulename}`;
    }

    if (completed && !!scheduled) {
      return `expired-${rulename}`;
    }

    return `inactive-${rulename}`;
  };

  const activeStatusMatches = (activeStatus, status) =>
    activeStatus === status || activeStatus?.startsWith(`${status}-`);

  update = async (skip) => {
    updating = true;

    if (!skip) {
      filtered = [];
      otherCampaigns = [];
      queryResult = await CampaignsService.getAll();

      if (!Array.isArray(queryResult)) {
        queryResult = [];
        updating = false;
        return;
      }

      queryResult = queryResult.map((item, index) => {
        item.order = index;

        item = determineCampaignStatus(item, targeting, integrations);
        item.activeStatus = assignActiveStatus(item);

        item.metrics = {};

        if (item.tags.length) {
          item.tags = item.tags.map((tag) => {
            if (!assignedTagColors[tag.text]) {
              assignedTagColors[tag.text] = tagColors.shift();
            }

            return {
              ...tag,
              backgroundColor: assignedTagColors[tag.text]
            };
          });
        }

        if (item.category) {
          item.categories = Object.keys(item.category).map((category) => {
            if (!assignedCategoryColors[category]) {
              assignedCategoryColors[category] = categoryColors.shift();
            }

            return {
              text: category,
              backgroundColor: assignedCategoryColors[category]
            };
          });
        }

        otherCampaigns.push(item);

        return item;
      });

      queryResult = [...otherCampaigns];
    }

    records = deepCopy(
      queryResult.map((item) => {
        item.range = { startDate, endDate };
        return item;
      })
    );

    const selectedStatus = Object.values(statusFilters || {})
      .map(({ enabled, value }) => (enabled ? value : false))
      .filter(Boolean);

    if (startDate && endDate) {
      records = records.filter(({ activeStatus, hasTargeting, rulename, schedule, enableschedule, notscheduled }) => {
        const start = new Date(schedule?.startDate);
        const end = new Date(schedule?.endDate);

        if (selectedItems.length && selectedItems.find(({ value }) => value === rulename)) {
          return true;
        }

        if (selectedStatus.includes('inactive') && activeStatusMatches(activeStatus, 'inactive')) {
          return true;
        }

        // Treat banners with scheduling disabled (enableschedule === false) as evergreen/unscheduled,
        // even if a stale schedule object lingers. notscheduled already === !enableschedule.
        if (includeEvergreen && notscheduled && hasTargeting) {
          return true;
        }

        // Only apply the schedule date-range test when scheduling is actually enabled.
        if (enableschedule && schedule) {
          return (
            new Date(startDate).setHours(0, 0, 0, 0) <= end && new Date(endDate).setHours(23, 59, 59, 999) >= start
          );
        }

        return false;

        // return (
        //   (!!~schedule && !!notscheduled && !!hasTargeting) ||
        //   (schedule && new Date(start).getTime() >= startDate) ||
        //   (schedule && start && endDate && start.getTime() >= startDate && end.getTime() <= endDate) ||
        //   (!selectedItems.length && created && created.getTime() >= startDate && created.getTime() <= endDate) ||
        //   (selectedItems.length && selectedItems.find(({ value }) => value === rulename))
        // );
      });
    }

    if (selectedStatus.length) {
      records = records.filter(({ activeStatus }) => {
        if (!activeStatus) return false;
        return selectedStatus.some((status) => activeStatusMatches(activeStatus, status));
      });
    }

    filtered = [...records];

    selectedData = [...filtered];

    getCompareItems(selectedData, selectedItemField);

    if (selectedItems.length) {
      await getCompareChartMetrics(selectedItems);
    }

    await getMetricsTotal(selectedData);

    selectedData = selectedData.map((campaign) => {
      const cacheEntry = cache[cacheKeyFor(campaign._id)];
      if (cacheEntry?.metrics) {
        campaign.metrics = cacheEntry.metrics;
      }
      return campaign;
    });

    updating = false;
  };

  const generateDateRange = (start, end) => {
    let rangeStartDate = new Date(start);

    const endDate = new Date(end);
    const dateRange = [];

    while (rangeStartDate <= endDate) {
      dateRange.push(rangeStartDate.getTime());
      rangeStartDate = subDays(rangeStartDate, -1);
    }

    return dateRange;
  };

  const backfillData = (metrics, dateRange) => {
    return dateRange.map((date) => {
      const existingData = metrics.find(
        (data) => formatDate(new Date(data[0]), 'MM/dd/yyyy') === formatDate(date, 'MM/dd/yyyy')
      );
      return existingData || [date, 0];
    });
  };

  const cacheKeyFor = (id) => `${id}${startDate}${endDate}${daysOverride || ''}${customStart || ''}${customEnd || ''}`;

  const getMetrics = async (campaign, isEdit) => {
    const id = campaign._id;
    const cacheKey = cacheKeyFor(id);

    if (!cache[cacheKey]) {
      // Fetches only far enough back to cover the window being rendered, then filters down
      // to it below. See resolveMetricsWindow for why the campaign lifetime is not used.
      const { start, days } = resolveMetricsWindow({
        campaign,
        fullHistory: isEdit,
        customStart,
        customEnd,
        daysOverride,
        startDate
      });
      const data = await CampaignsService.getMetrics({ aid: id, days });

      let filteredData = data;

      if (!isEditView || daysOverride || isCustomRange) {
        const rangeEnd = isCustomRange ? endOfDay(new Date(customEnd)) : daysOverride ? new Date() : new Date(endDate);
        filteredData = data.filter((item) => {
          const timesliceDate = new Date(item.timeslice);
          return new Date(timesliceDate) >= new Date(start) && new Date(timesliceDate) <= rangeEnd;
        });
      }

      const metrics = consolidateMetrics(filteredData, campaign);

      cache[cacheKey] = {
        metrics: { ...metrics, disableviews: campaign.disableviews },
        timestamp: Date.now()
      };

      // Cleanup cache periodically
      cleanupExpiredCacheEntries();
      cleanupOldCacheEntries();
    }

    const cacheEntry = cache[cacheKey];
    if (cacheEntry?.metrics) {
      campaign.metrics = cacheEntry.metrics;
    }

    return cacheEntry?.metrics;
  };

  const createSparkChart = (data) => {
    let deliveriesSeries = [];
    let clicksSeries = [];
    let viewsSeries = [];
    let basicClicksSeries = [];
    let basicViewsSeries = [];

    data.forEach((campaign) => {
      const cacheEntry = cache[cacheKeyFor(campaign._id)];
      if (!cacheEntry?.metrics) {
        console.warn(`Missing cache entry for campaign ${campaign._id}`);
        return;
      }

      const {
        combinedClicks: clicks,
        basicclicks,
        deliveries,
        combinedViews: views,
        trackedBasicViews: basicviews
      } = cacheEntry.metrics;

      deliveriesSeries = [...deliveriesSeries, ...deliveries];
      clicksSeries = [...clicksSeries, ...clicks];
      viewsSeries = [...viewsSeries, ...views];
      basicClicksSeries = [...basicClicksSeries, ...basicclicks];
      basicViewsSeries = [...basicViewsSeries, ...basicviews];
    });

    let config = deepCopy(sparkChartData);

    config.delivered.data = generateMetricDataset(
      sumMetricSeries(deliveriesSeries),
      config.delivered.label,
      config.delivered.color
    );
    config.views.data = generateMetricDataset(sumMetricSeries(viewsSeries), config.views.label, config.views.color);
    config.clicks.data = generateMetricDataset(sumMetricSeries(clicksSeries), config.clicks.label, config.clicks.color);
    config.basicClicks.data = generateMetricDataset(
      sumMetricSeries(basicClicksSeries),
      config.basicClicks.label,
      config.basicClicks.color
    );
    config.basicViews.data = generateMetricDataset(
      sumMetricSeries(basicViewsSeries),
      config.basicViews.label,
      config.basicViews.color
    );

    sparkData = { ...config };
  };

  const getMetricsTotal = async (data, isEdit) => {
    campaignMetrics = {
      totalDeliveries: 0,
      totalClicks: 0,
      totalViews: 0,
      totalReach: 0,
      totalBasicClicks: 0,
      totalBasicViews: 0
    };

    let metrics = await mapWithConcurrency(
      data,
      (campaign) => getMetrics(campaign, isEdit),
      METRICS_REQUEST_CONCURRENCY
    );

    createSparkChart(data);

    const totals = summarizeMetrics(metrics);
    campaignMetrics = {
      totalDeliveries: totals.ad,
      totalClicks: totals.combinedClicks,
      totalViews: totals.combinedViews,
      totalReach: totals.uniques,
      totalBasicClicks: totals.basicadclick,
      totalBasicViews: metrics.reduce(
        (sum, report) => sum + report.trackedBasicViews.reduce((count, [, value]) => count + value, 0),
        0
      ),
      ctr: totals.combinedCtr,
      ctrr: totals.ctrr,
      basicCtr: totals.basicCtr
    };

    return campaignMetrics;
  };

  const selectField = async (field) => {
    campaignFields = campaignFields.map((item) => {
      item.selected = field.value === item.value;

      return item;
    });

    selectedField = field;
    selectedItemField = selectedField.value;

    isExpanded = false;
    selectedData = [];
    selectedItems = [];

    onClearChartMetrics();

    await update(true);
  };

  const generateUniqueItems = (items, uniqueSet, isSelected) => {
    return items
      .filter((item) => !uniqueSet.has(item) && uniqueSet.add(item))
      .map((item) => ({
        label: item,
        value: item,
        checked: isSelected(item)
      }));
  };

  const buildFolderMaps = (folders) => {
    folderNameMap = {};
    folderParentMap = {};

    folders.forEach((folder) => {
      folderNameMap[folder.id] = folder.text;
      folderParentMap[folder.id] = folder.parent;
    });
  };

  // Returns a campaign folder's chain (its own folder + every ancestor folder), each entry
  // carrying the folder `name` and its full `path` (e.g. "Marketing / Q1 Promos"). Including
  // the ancestors means selecting a parent folder also matches campaigns in its subfolders.
  const getFolderChain = (folderid) => {
    const ids = [];
    const seen = new Set();

    let current = folderid;

    while (current && current !== '#' && folderNameMap[current] && !seen.has(current)) {
      seen.add(current);
      ids.push(current);
      current = folderParentMap[current];
    }

    return ids.map((id, index) => ({
      name: folderNameMap[id],
      path: ids
        .slice(index)
        .map((ancestorId) => folderNameMap[ancestorId])
        .reverse()
        .join(' / ')
    }));
  };

  const getFolderNames = (folderid) => getFolderChain(folderid).map(({ name }) => name);

  const getCompareItems = (data, field) => {
    let items = [];

    const uniqueSet = new Set();

    if (field === 'campaigns') {
      items = data.map(({ rulename }) => ({
        label: rulename,
        value: rulename,
        checked: selectedItems.includes(rulename)
      }));
    }
    if (field === 'folders') {
      items = Object.keys(folderNameMap)
        .map((id) => getFolderChain(id)[0])
        .filter(({ name }) => !uniqueSet.has(name) && uniqueSet.add(name))
        .map(({ name, path }) => ({
          label: path,
          value: name,
          checked: selectedItems.includes(name)
        }));
    }

    if (field === 'tags') {
      items = data.flatMap(({ tags }) =>
        tags
          ? generateUniqueItems(
              tags.map((tag) => tag.text),
              uniqueSet,
              (text) => selectedItems.includes(text)
            )
          : []
      );
    }

    if (field === 'categories') {
      items = data.flatMap(({ category }) =>
        category
          ? generateUniqueItems(Object.keys(category), uniqueSet, (categoryValue) =>
              selectedItems.includes(categoryValue)
            )
          : []
      );
    }

    campaigns = items.sort((a, b) => a.label.localeCompare(b.label));
  };

  const setSelectedData = (field, data, filteredData, values, items) => {
    if (field === 'campaigns') {
      data = filteredData.filter((item) => values.includes(item.rulename));
      data = items.map((item) => data.find((campaign) => campaign.rulename === item.value));
    } else if (field === 'folders') {
      data = filteredData.filter((item) => getFolderNames(item.folderid).some((name) => values.includes(name)));
      data = items.flatMap((item) => data.filter((campaign) => getFolderNames(campaign.folderid).includes(item.value)));
    } else if (field === 'tags') {
      data = filteredData.filter((item) => item.tags.some((tag) => values.includes(tag.text)));
      data = items.flatMap((item) =>
        data.filter((campaign) => campaign.tags.filter((tag) => tag.text === item.value).length && campaign)
      );
    } else if (field === 'categories') {
      data = filteredData.filter((item) =>
        Object.keys(item.category || {}).some((category) => values.includes(category))
      );
    }

    return data;
  };

  const consolidateCompareChartMetrics = (data, selected, values) => {
    const initializeMetrics = () => ({
      deliveries: [],
      views: [],
      clicks: [],
      basicviews: [],
      basicclicks: [],
      combinedViews: [],
      combinedClicks: [],
      trackedBasicViews: []
    });

    const updateMetrics = (acc, key, metrics) => {
      if (!acc[key]) {
        acc[key] = initializeMetrics();
      }

      Object.keys(metrics).forEach((metric) => {
        if (acc[key][metric]) {
          acc[key][metric].push(...metrics[metric]);
        }
      });
    };

    return data.reduce((acc, curr, index) => {
      const { rulename, tags, category, folderid } = selected[index];

      if (rulename && values.includes(rulename)) {
        updateMetrics(acc, rulename, curr);
      }

      getFolderNames(folderid).forEach((name) => {
        if (name && values.includes(name)) {
          updateMetrics(acc, name, curr);
        }
      });

      if (tags) {
        tags.forEach(({ text }) => {
          if (text && values.includes(text)) {
            updateMetrics(acc, text, curr);
          }
        });
      }

      if (category) {
        Object.keys(category).forEach((categoryValue) => {
          if (categoryValue && values.includes(categoryValue)) {
            updateMetrics(acc, categoryValue, curr);
          }
        });
      }

      return acc;
    }, {});
  };

  const sortCompareChartMetrics = (data, field) => {
    Object.keys(data).forEach((key) => {
      data[key].deliveries.sort((a, b) => a[0] - b[0]);
      data[key].views.sort((a, b) => a[0] - b[0]);
      data[key].clicks.sort((a, b) => a[0] - b[0]);
      data[key].basicviews.sort((a, b) => a[0] - b[0]);
      data[key].basicclicks.sort((a, b) => a[0] - b[0]);

      if (field === 'tags' || field === 'categories' || field === 'folders') {
        const metrics = [
          'deliveries',
          'views',
          'clicks',
          'basicviews',
          'basicclicks',
          'combinedViews',
          'combinedClicks',
          'trackedBasicViews'
        ];

        metrics.forEach((metric) => {
          const aggregatedData = {};

          data[key][metric].forEach((dataPoint) => {
            const [x, y] = dataPoint;

            if (!aggregatedData[x]) {
              aggregatedData[x] = 0;
            }

            aggregatedData[x] += y;
          });

          data[key][metric] = Object.entries(aggregatedData).map(([x, y]) => [parseInt(x), y]);
        });
      }
    });

    return data;
  };

  const generateCompareChartDatasets = (data, values) => {
    if (!isEditView) {
      Object.values(data).forEach((metric, index) => {
        const propertyName = `--dataset-color-${index}`;
        document.documentElement.style.setProperty(propertyName, PERFORMANCE_COLORS[index]);
        appliedCssProperties.add(propertyName);
      });
    }

    return processChartDataset(Object.values(data), compareChartMetrics, values, PERFORMANCE_COLORS);
  };

  const getCompareChartMetrics = async (data) => {
    if (!data) {
      onClearChartMetrics();

      campaignMetrics = {
        totalDeliveries: 0,
        totalClicks: 0,
        totalViews: 0,
        totalReach: 0,
        totalBasicClicks: 0,
        totalBasicViews: 0
      };

      selectedItems = [];

      selectedData = [];

      await update();

      return;
    }

    updating = true;

    getCompareItems(filtered, selectedItemField);

    selectedItems = data;

    let selectedValues = data.map((item) => item.value);

    selectedData = setSelectedData(selectedItemField, selectedData, filtered, selectedValues, selectedItems);

    let metrics = await mapWithConcurrency(
      selectedData,
      (campaign) => getMetrics(campaign),
      METRICS_REQUEST_CONCURRENCY
    );

    selectedData = selectedData.map((campaign) => {
      const cacheEntry = cache[cacheKeyFor(campaign._id)];
      if (cacheEntry?.metrics) {
        campaign.metrics = cacheEntry.metrics;
      }
      return campaign;
    });

    expandedChartData = metrics;

    metrics = consolidateCompareChartMetrics(metrics, selectedData, selectedValues);

    metrics = sortCompareChartMetrics(metrics, selectedItemField);

    compareChartMetrics = generateCompareChartDatasets(metrics, selectedValues);

    if (data.length >= 7) {
      campaigns = [];
    }

    if (!isEditView) {
      await getMetricsTotal(selectedData);
    }

    updating = false;

    selectedData = [...selectedData];

    if (isExpanded === true) {
      processChartDataset(expandedChartData, expandedChartMetrics, selectedData, COLORS);
      expandedChartMetrics = { ...expandedChartMetrics };
    }

    return compareChartMetrics;
  };

  const groupByMonth = (series) => {
    const data = series.reduce((acc, curr) => {
      const formattedLabel = formatDate(curr[0], 'MMM, 1 yyyy');

      if (!acc[formattedLabel]) {
        acc[formattedLabel] = 0;
      }

      acc[formattedLabel] += curr[1];

      return acc;
    }, {});

    const sortedSeries = Object.keys(data)
      .map((dateString) => new Date(dateString))
      .sort((a, b) => a - b);

    const sortedSeriesObject = sortedSeries.reduce((acc, date) => {
      const dateString = formatDate(date, 'MMM, 1 yyyy');
      acc[dateString] = data[dateString];
      return acc;
    }, {});

    return Object.keys(sortedSeriesObject).map((date) => ({
      x: new Date(date).getTime(),
      y: sortedSeriesObject[date]
    }));
  };

  const adjustGrandularity = (data) => (chartUnit === 'day' ? data.map(([x, y]) => ({ x, y })) : groupByMonth(data));

  const populateEditViewDatasets = (metric, deliveries, impressions, clicks, basicImpressions, basicClicks) => {
    deliveries.push({
      label: $format('label.DELIVERIES'),
      fill: true,
      tension: 0.2,
      borderColor: '#43A0FC',
      backgroundColor: chartType === 'bar' ? '#43A0FC' : '#43A0FC10',
      data: adjustGrandularity(metric.deliveries)
    });

    impressions.push({
      label: $format('label.IMPRESSIONS'),
      fill: true,
      tension: 0.2,
      borderColor: '#D9BC61',
      backgroundColor: chartType === 'bar' ? '#D9BC61' : '#D9BC6110',
      data: adjustGrandularity(metric.impressions)
    });

    clicks.push({
      label: $format('label.CLICKS'),
      fill: true,
      tension: 0.2,
      borderColor: '#92E2C0',
      backgroundColor: chartType === 'bar' ? '#92E2C0' : '#92E2C010',
      data: adjustGrandularity(metric.clicks)
    });

    basicImpressions.push({
      label: $format('label.IMPRESSIONS_BASIC'),
      fill: true,
      tension: 0.2,
      borderColor: '#D9BC61',
      backgroundColor: chartType === 'bar' ? '#D9BC61' : '#D9BC6110',
      data: adjustGrandularity(metric.basicImpressions)
    });

    basicClicks.push({
      label: $format('label.CLICKS_BASIC'),
      fill: true,
      tension: 0.2,
      borderColor: '#92E2C0',
      backgroundColor: chartType === 'bar' ? '#92E2C0' : '#92E2C010',
      data: adjustGrandularity(metric.basicClicks)
    });

    return { deliveries, impressions, clicks, basicImpressions, basicClicks };
  };

  const processChartDataset = (data, metricsObject, selected = [], colors = []) => {
    if (isExpanded) {
      selected = selected.map(({ rulename }) => rulename);
    }

    let startDateRange = formatDate(new Date(startDate), 'MM/dd/yyyy');
    let endDateDateRange = formatDate(new Date(endDate), 'MM/dd/yyyy');

    const dateRange = generateDateRange(startDateRange, endDateDateRange);

    let deliveriesDatasets = [];
    let impressionsDatasets = [];
    let clicksDatasets = [];
    let basicImpressionsDatasets = [];
    let basicClicksDatasets = [];

    if (data.length) {
      data.forEach((metric, index) => {
        let datasetConfig = {
          label: selected[index],
          fill: true,
          tension: 0.2,
          borderColor: colors[index],
          backgroundColor: chartType === 'bar' ? colors[index] : `${colors[index]}10`
        };

        const deliveries = filterDataByRange(backfillData(sumMetricSeries(metric.deliveries), dateRange));
        const impressions = filterDataByRange(backfillData(sumMetricSeries(metric.combinedViews), dateRange));
        const clicks = filterDataByRange(backfillData(sumMetricSeries(metric.combinedClicks), dateRange));
        const basicImpressions = filterDataByRange(backfillData(sumMetricSeries(metric.trackedBasicViews), dateRange));
        const basicClicks = filterDataByRange(backfillData(sumMetricSeries(metric.basicclicks), dateRange));

        if (isEditView) {
          populateEditViewDatasets(
            { deliveries, impressions, clicks, basicImpressions, basicClicks },
            deliveriesDatasets,
            impressionsDatasets,
            clicksDatasets,
            basicImpressionsDatasets,
            basicClicksDatasets
          );
        } else {
          deliveriesDatasets.push({
            ...datasetConfig,
            data: adjustGrandularity(deliveries)
          });

          impressionsDatasets.push({
            ...datasetConfig,
            data: adjustGrandularity(impressions)
          });

          clicksDatasets.push({
            ...datasetConfig,
            data: adjustGrandularity(clicks)
          });

          basicImpressionsDatasets.push({
            ...datasetConfig,
            data: adjustGrandularity(basicImpressions)
          });

          basicClicksDatasets.push({
            ...datasetConfig,
            data: adjustGrandularity(basicClicks)
          });
        }
      });
    }

    metricsObject.deliveries = { datasets: deliveriesDatasets };
    metricsObject.impressions = { datasets: impressionsDatasets };
    metricsObject.clicks = { datasets: clicksDatasets };
    metricsObject.basicClicks = { datasets: basicClicksDatasets };
    metricsObject.basicImpressions = { datasets: basicImpressionsDatasets };

    return metricsObject;
  };

  const onEvergreenChange = async () => {
    await update();
  };

  const onDateChange = async (dateRange) => {
    if ((dateRange.startDate && dateRange.endDate) || (!dateRange.startDate && !dateRange.endDate)) {
      startDate = filters.dateRange.startDate = dateRange.startDate;
      endDate = filters.dateRange.endDate = dateRange.endDate;
      filters = { ...filters };
      await update();
    }
  };

  const onExpandCharts = () => {
    isExpanded = !isExpanded;

    if (isExpanded === true) {
      processChartDataset(expandedChartData, expandedChartMetrics, selectedData, COLORS);
      expandedChartMetrics = { ...expandedChartMetrics };
    } else {
      getCompareChartMetrics(selectedItems);
    }
  };

  let initialized = false;

  onMount(async () => {
    updating = true;

    targeting = await TargetingService.get();
    integrations = await PluginsService.getAll();

    buildFolderMaps(await AssetsService.getAssetFolders());

    updating = false;

    if (isEditView) {
      if (daysOverride === null) {
        daysOverride = determineDefaultRange(filtered[0]);
      }
      await getCompareChartMetrics(selectedItems);
      await getMetricsTotal(selectedData, true);
    } else {
      await update();
    }

    initialized = true;
  });

  onDestroy(() => {
    // Clean up CSS custom properties
    appliedCssProperties.forEach((propertyName) => {
      document.documentElement.style.removeProperty(propertyName);
    });
    appliedCssProperties.clear();

    // Clear large data structures to help with garbage collection
    compareChartMetrics = {
      deliveries: [],
      impressions: [],
      clicks: [],
      basicClicks: [],
      basicImpressions: []
    };

    expandedChartMetrics = {
      deliveries: [],
      impressions: [],
      clicks: [],
      basicClicks: [],
      basicImpressions: []
    };

    expandedChartData = {};
    queryResult = [];
    campaigns = [];
    otherCampaigns = [];
    records = [];
    selectedData = [];
    filtered = [];

    // Clean up color arrays
    assignedTagColors = {};
    assignedCategoryColors = {};
  });

  const toggleDatePicker = () => (isOpen = !isOpen);
  const formatDates = (dateString) => (dateString && formatDate(new Date(dateString), dateFormat)) || '';

  const refetchOnRangeChange = async (_override, _start, _end) => {
    if (!initialized || !isEditView) return;
    updating = true;
    await getCompareChartMetrics(selectedItems);
    await getMetricsTotal(selectedData, true);
    updating = false;
  };

  $: formattedStartDate = formatDates(startDate);
  $: formattedEndDate = formatDates(endDate);
  $: initialized && onEvergreenChange(includeEvergreen);
  $: refetchOnRangeChange(daysOverride, customStart, customEnd);
</script>

<div id="marketing-performance" class="page">
  {#if !isEditView}
    <div class="context-bar">
      <div class="comparison-select">
        <Select
          bind:items={campaigns}
          bind:value={selectedItems}
          on:change={getCompareChartMetrics(selectedItems)}
          on:clear={getCompareChartMetrics(selectedItems)}
          placeholder={selectedItems.length >= 7
            ? ''
            : $format('label.PLACEHOLDER.SELECT_ITEMS_TO_COMPARE', { values: { items: selectedItemField } })}
          clearable={false}
          searchable
          multiple
        />
        <div class="category-dropdown">
          <Dropdown class="field-dropdown">
            <DropdownToggle tag="div" tabindex="0" caret>{selectedField?.label || ''}</DropdownToggle>
            <DropdownMenu>
              {#each campaignFields as field}
                <DropdownItem active={field.value === selectedField?.value} on:click={() => selectField(field)}>
                  {field.label}
                </DropdownItem>
              {/each}
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>
      <div class="date-filter">
        <DatePicker
          isRange
          align="right"
          isMultipane
          showPresets
          bind:isOpen
          bind:startDate
          bind:endDate
          {onDateChange}
        >
          <div class="date-field" on:click={toggleDatePicker} class:open={isOpen} role="button" tabindex="0">
            <Icon name="calendar" />
            <div class="date">
              {#if startDate}
                {formattedStartDate} - {formattedEndDate}
              {:else}
                {$format('label.PICK_A_DATE')}
              {/if}
            </div>
          </div>
        </DatePicker>
      </div>
    </div>
  {/if}

  <div class="metrics-summary-wrap">
    <slot name="summary" />
    {#if selectedItems.length || isEditView}
      <div class="controls">
        {#if (selectedItemField === 'tags' || selectedItemField === 'categories' || selectedItemField === 'folders') && selectedItems.length}
          <span class="hide--mobile">{$format('label.DISPLAY_MODE')}</span>

          <ButtonGroup size="sm">
            <Button active={isExpanded !== true} on:click={onExpandCharts}>{$format('label.GROUPED')}</Button>
            <Button active={isExpanded === true} on:click={onExpandCharts}>{$format('label.INDIVIDUAL')}</Button>
          </ButtonGroup>
        {/if}

        {#if selectedItems.length}
          <span class="hide--mobile">{$format('label.GRANULARITY')}</span>

          <ButtonGroup size="sm">
            <Button
              active={chartUnit === 'day'}
              on:click={() => {
                chartUnit = 'day';
                getCompareChartMetrics(selectedItems);
              }}
            >
              {$format('label.DAY')}
            </Button>
            <Button
              active={chartUnit !== 'day'}
              on:click={() => {
                chartUnit = 'month';
                getCompareChartMetrics(selectedItems);
              }}
            >
              {$format('label.MONTH')}
            </Button>
          </ButtonGroup>

          <span class="hide--mobile">{$format('label.CHART_TYPE')}</span>
          <ButtonGroup size="sm">
            <Button
              active={chartType === 'line'}
              on:click={() => {
                chartType = 'line';
                getCompareChartMetrics(selectedItems);
              }}
            >
              <Icon name="chart-line" />
            </Button>
            <Button
              active={chartType === 'bar'}
              on:click={() => {
                chartType = 'bar';
                getCompareChartMetrics(selectedItems);
              }}
            >
              <Icon name="chart-bar" />
            </Button>
          </ButtonGroup>
        {/if}

        {#if isEditView}
          <span class="hide--mobile">
            {$format('label.RANGE')}
            <Icon id="tooltip-chart-range" name="info" />
            <Tooltip target="tooltip-chart-range" placement="top">
              {$format('label.TOOLTIP.CAMPAIGN_CHART_RANGE_EDIT')}
            </Tooltip>
          </span>

          <DatePicker
            isRange
            isMultipane
            align="right"
            bind:isOpen={customPickerOpen}
            startDate={customStart}
            endDate={customEnd}
            onDateChange={onCustomDateChange}
          >
            <ButtonGroup size="sm">
              <Button
                id="tooltip-custom-range"
                active={isCustomRange}
                on:click={() => (customPickerOpen = !customPickerOpen)}
              >
                {$format('label.CUSTOM')}
              </Button>
              {#each rangeOptions as option}
                <Button
                  active={!isCustomRange && daysOverride === option.value}
                  on:click={() => selectPreset(option.value)}
                >
                  {option.label}
                </Button>
              {/each}
            </ButtonGroup>
          </DatePicker>
          <Tooltip target="tooltip-custom-range" placement="top">
            {#if isCustomRange}
              {formatDates(customStart)} - {formatDates(customEnd)}
            {:else}
              {$format('label.TOOLTIP.CAMPAIGN_CHART_RANGE_CUSTOM')}
            {/if}
          </Tooltip>
        {/if}
      </div>
    {/if}

    <div class="metrics-summary" class:selections={selectedItems.length} class:bar={chartType === 'bar'}>
      <div class="metric">
        <div class="metric-label">
          <span>{$format('label.DELIVERIES')}</span>
          <Icon name="help-circle" id="tooltip-campaign-deliveries" />
          <Tooltip target="tooltip-campaign-deliveries" placement="top">
            {@html $format('label.EMAILS_DELIVERED')}
            <div>{$format('label.TOOLTIP.EMAILS_DELIVERED')}</div>
          </Tooltip>
        </div>
        <div class="metric-value" title={campaignMetrics.totalDeliveries.toLocaleString()}>
          {shortenNumber(campaignMetrics.totalDeliveries, 1)}
          {#if !selectedItems.length && sparkData?.delivered?.data?.datasets}
            <SparkChart bind:data={sparkData.delivered.data} formatHeader={sparkFormatHeader} height="25px" />
          {/if}
        </div>
        {#if selectedItems.length}
          {#if compareChartMetrics?.deliveries?.datasets?.length || expandedChartMetrics?.deliveries?.datasets?.length}
            {#if isExpanded}
              <Chart bind:type={chartType} bind:data={expandedChartMetrics.deliveries} bind:unit={chartUnit} />
            {:else}
              <Chart bind:type={chartType} bind:data={compareChartMetrics.deliveries} bind:unit={chartUnit} />
            {/if}
          {/if}
        {/if}
      </div>
      <div class="metric">
        <div class="metric-label">
          <span>{$format('label.IMPRESSIONS')}</span>
          <Icon name="help-circle" id="tooltip-campaign-views" />
          <Tooltip target="tooltip-campaign-views" placement="top">
            <b>{$format('label.IMPRESSIONS')}</b>
            <div>
              {$format('label.TOOLTIP.IMPRESSIONS')}
            </div>
          </Tooltip>
          {#if isEditView && selectedData[0]?.disableviews}
            <Icon id="disabled-views-warning" name="alert-triangle" />

            <Tooltip target="disabled-views-warning" placement="right">
              {$format('label.TOOLTIP.DISABLED_VIEWS')}
            </Tooltip>
          {/if}
        </div>
        <div class="metric-value" title={campaignMetrics.totalViews.toLocaleString()}>
          {shortenNumber(campaignMetrics.totalViews, 1)}
          {#if !selectedItems.length && sparkData?.views?.data?.datasets}
            <SparkChart bind:data={sparkData.views.data} formatHeader={sparkFormatHeader} height="25px" />
          {/if}
        </div>
        {#if selectedItems.length}
          {#if compareChartMetrics?.impressions?.datasets?.length || expandedChartMetrics?.impressions?.datasets?.length}
            {#if isExpanded}
              <Chart bind:type={chartType} bind:data={expandedChartMetrics.impressions} bind:unit={chartUnit} />
            {:else}
              <Chart bind:type={chartType} bind:data={compareChartMetrics.impressions} bind:unit={chartUnit} />
            {/if}
          {/if}
        {/if}
      </div>
      <div class="metric">
        <div class="metric-label">
          <span>{$format('label.CLICKS')}</span>
          <Icon name="help-circle" id="tooltip-campaign-clicks" />
          <Tooltip target="tooltip-campaign-clicks" placement="top">
            <b>{$format('label.CLICKS')}</b>
            <div>
              {$format('label.TOOLTIP.CLICKS')}
            </div>
          </Tooltip>
        </div>
        <div class="metric-value" title={campaignMetrics.totalClicks.toLocaleString()}>
          {shortenNumber(campaignMetrics.totalClicks, 1)}
          {#if !selectedItems.length && sparkData?.clicks?.data?.datasets}
            <SparkChart bind:data={sparkData.clicks.data} formatHeader={sparkFormatHeader} height="25px" />
          {/if}
        </div>
        {#if selectedItems.length}
          {#if compareChartMetrics?.clicks?.datasets?.length || expandedChartMetrics?.clicks?.datasets?.length}
            {#if isExpanded}
              <Chart bind:type={chartType} bind:data={expandedChartMetrics.clicks} bind:unit={chartUnit} />
            {:else}
              <Chart bind:type={chartType} bind:data={compareChartMetrics.clicks} bind:unit={chartUnit} />
            {/if}
          {/if}
        {/if}
      </div>
      {#if !selectedItems.length}
        <div class="metric">
          <div class="metric-label">
            <span>CTR</span>
            <Icon name="help-circle" id="tooltip-campaign-ctr" />
            <Tooltip target="tooltip-campaign-ctr" placement="top">
              <b>{$format('label.CLICK_THROUGH_RATE')}</b>
              <div>{$format('label.TOOLTIP.CLICK_THROUGH_RATE')}</div>
            </Tooltip>
          </div>
          <div class="metric-value ctr">
            {formatRate(campaignMetrics.ctr)}
          </div>
        </div>
        <div class="metric">
          <div class="metric-label">
            <span>CTRR</span>
            <Icon name="help-circle" id="tooltip-campaign-ctrr" />
            <Tooltip target="tooltip-campaign-ctrr" placement="top">
              <b>{$format('label.CLICK_TO_RECIPIENT_RATE')}</b>
              <div>{$format('label.TOOLTIP.CLICK_TO_RECIPIENT_RATE')}</div>
            </Tooltip>
          </div>
          <div class="metric-value ctrr">
            {formatRate(campaignMetrics.ctrr)}
          </div>
        </div>
      {/if}
      <div class="metric reach" class:selected={selectedItems.length}>
        <div class="reach-metric">
          <div class="metric-label">
            <span>{$format('label.REACH')}</span>
            <Icon name="help-circle" id="tooltip-campaign-reach" />
            <Tooltip target="tooltip-campaign-reach" placement="top">
              <b>{$format('label.REACH')}</b>
              <div>{$format('label.TOOLTIP.REACH')}</div>
            </Tooltip>
          </div>
          <div class="metric-value reach" title={campaignMetrics.totalReach.toLocaleString()}>
            {shortenNumber(campaignMetrics.totalReach, 1)}
          </div>
        </div>
        {#if selectedItems.length}
          <div class="ctr-metric">
            <div class="metric-label">
              <span>CTR</span>
              <Icon name="help-circle" id="tooltip-campaign-ctr-basic" />
              <Tooltip target="tooltip-campaign-ctr-basic" placement="top">
                <b>{$format('label.CLICK_THROUGH_RATE')}</b>
                <div>{$format('label.TOOLTIP.CLICK_THROUGH_RATE')}</div>
              </Tooltip>
              {#if selectedData.length === 1 && selectedData[0]?.disableviews}
                <Icon id="no-ctr-warning" name="alert-triangle" />

                <Tooltip target="no-ctr-warning" placement="right">
                  {$format('label.TOOLTIP.NO_CTR')}
                </Tooltip>
              {/if}
            </div>
            <div class="metric-value ctr">
              {formatRate(campaignMetrics.ctr)}
            </div>
          </div>
          <div class="ctrr-metric">
            <div class="metric-label">
              <span>CTRR</span>
              <Icon name="help-circle" id="tooltip-campaign-ctrr-basic" />
              <Tooltip target="tooltip-campaign-ctrr-basic" placement="top">
                <b>{$format('label.CLICK_TO_RECIPIENT_RATE')}</b>
                <div>{$format('label.TOOLTIP.CLICK_TO_RECIPIENT_RATE')}</div>
              </Tooltip>
            </div>
            <div class="metric-value ctr">
              {formatRate(campaignMetrics.ctrr)}
            </div>
          </div>
        {/if}
      </div>
    </div>
    {#if campaignMetrics.totalBasicViews || campaignMetrics.totalBasicClicks}
      <div class="metrics-summary basic" class:selections={selectedItems.length} class:bar={chartType === 'bar'}>
        <div class="metric">
          <div class="metric-label">
            <span>{$format('label.IMPRESSIONS_BASIC')}</span>
            <Icon name="help-circle" id="tooltip-campaign-views-basic" />
            <Tooltip target="tooltip-campaign-views-basic" placement="top">
              <b>{$format('label.IMPRESSIONS_BASIC')}</b>
              <div>
                {$format('label.TOOLTIP.IMPRESSIONS_BASIC')}
              </div>
            </Tooltip>
          </div>
          <div class="metric-value" title={campaignMetrics.totalBasicViews.toLocaleString()}>
            {shortenNumber(campaignMetrics.totalBasicViews, 1)}
            {#if !selectedItems.length && sparkData?.basicViews?.data?.datasets}
              <SparkChart bind:data={sparkData.basicViews.data} formatHeader={sparkFormatHeader} height="25px" />
            {/if}
          </div>
          {#if selectedItems.length}
            {#if compareChartMetrics?.basicImpressions?.datasets?.length || expandedChartMetrics?.basicImpressions?.datasets?.length}
              {#if isExpanded}
                <Chart bind:type={chartType} bind:data={expandedChartMetrics.basicImpressions} bind:unit={chartUnit} />
              {:else}
                <Chart bind:type={chartType} bind:data={compareChartMetrics.basicImpressions} bind:unit={chartUnit} />
              {/if}
            {/if}
          {/if}
        </div>
        <div class="metric">
          <div class="metric-label">
            <span>{$format('label.CLICKS_BASIC')}</span>
            <Icon name="help-circle" id="tooltip-campaign-clicks-basic" />
            <Tooltip target="tooltip-campaign-clicks-basic" placement="top">
              <b>{$format('label.CLICKS_BASIC')}</b>
              <div>
                {$format('label.TOOLTIP.CLICKS_BASIC')}
              </div>
            </Tooltip>
          </div>
          <div class="metric-value" title={campaignMetrics.totalBasicClicks.toLocaleString()}>
            {shortenNumber(campaignMetrics.totalBasicClicks, 1)}
            {#if !selectedItems.length && sparkData?.basicClicks?.data?.datasets}
              <SparkChart bind:data={sparkData.basicClicks.data} formatHeader={sparkFormatHeader} height="25px" />
            {/if}
            {#if selectedItems.length}
              <div class="pill" id="tooltip-campaign-clicks-ctr-basic">
                <span>{formatRate(campaignMetrics.basicCtr)}</span>
                <span>CTR</span>
                <Tooltip target="tooltip-campaign-clicks-ctr-basic" placement="top">
                  <b>{$format('label.CLICK_THROUGH_RATE')}</b>
                  <div>{$format('label.TOOLTIP.CLICK_THROUGH_RATE')}</div>
                </Tooltip>
              </div>
            {/if}
          </div>
          {#if selectedItems.length}
            {#if compareChartMetrics?.basicClicks?.datasets?.length || expandedChartMetrics?.basicClicks?.datasets?.length}
              {#if isExpanded}
                <Chart bind:type={chartType} bind:data={expandedChartMetrics.basicClicks} bind:unit={chartUnit} />
              {:else}
                <Chart bind:type={chartType} bind:data={compareChartMetrics.basicClicks} bind:unit={chartUnit} />
              {/if}
            {/if}
          {/if}
        </div>
        {#if !selectedItems.length}
          <div class="metric">
            <div class="metric-label">
              <span>CTR</span>
              <Icon name="help-circle" id="tooltip-campaign-ctr-basic" />
              <Tooltip target="tooltip-campaign-ctr-basic" placement="top">
                <b>{$format('label.CLICK_THROUGH_RATE')}</b>
                <div>{$format('label.TOOLTIP.CLICK_THROUGH_RATE')}</div>
              </Tooltip>
            </div>
            <div class="metric-value">
              {formatRate(campaignMetrics.basicCtr)}
            </div>
          </div>
        {/if}
      </div>
    {/if}
  </div>
</div>

<style lang="scss" src="./performance-insights.scss"></style>
